import { useMemo } from 'react';

/**
 * Custom hook for Contact Request table columns.
 * Provides column definitions based on user role and available data.
 */
const useContactRequestColumns = ({ isAdmin, countries }) => {
  // Helper to format date (consistent with Diet Requests table)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(navigator.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Helper to get country name from country code
  const getCountryName = (countryCode) => {
    if (!countryCode || !countries || !Array.isArray(countries)) return countryCode || '';
    const country = countries.find(c => c.key === countryCode);
    return country ? country.value : countryCode;
  };

  // Table columns definition
  const columns = useMemo(() => {
    const cols = [];

    // Date column
    cols.push({
      accessorKey: 'creationDate',
      header: 'DATE',
      size: 250,
      minSize: 220,
      cell: info => formatDate(info.getValue()),
    });

    // User column (combined name and email)
    cols.push({
      id: 'user',
      header: 'USER',
      size: 300,
      minSize: 250,
      accessorFn: row => `${row.firstName || ''} ${row.lastName || ''}`.trim(),
      cell: info => {
        const row = info.row.original;
        const name = `${row.firstName || ''} ${row.lastName || ''}`.trim();
        const email = row.email || '';

        if (!name && !email) return '-';

        if (!email) {
          return (
            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200">
              {name}
            </div>
          );
        }

        return (
          <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200">
            <span className="mr-1">{name || 'User'}</span>
            <span>&lt;</span>
            <a
              href={`mailto:${email}`}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-4"
              title={`Send email to ${email}`}
            >
              {email}
            </a>
            <span>&gt;</span>
          </div>
        );
      },
    });

    // Diet Request ID column - Admin only
    if (isAdmin) {
      cols.push({
        accessorKey: 'recommendationId',
        header: 'DIET REQUEST ID',
        size: 350,
        minSize: 350,
        cell: info => info.getValue() || '-',
      });
    }

    // Country column (nested in recommendation)
    cols.push({
      id: 'country',
      header: 'COUNTRY',
      size: 180,
      minSize: 150,
      accessorFn: row => row.recommendation?.country,
      cell: info => getCountryName(info.getValue()) || '-',
    });

    // Profile column - admin only (nested in recommendation)
    if (isAdmin) {
      cols.push({
        id: 'profileName',
        header: 'CLIENT',
        size: 200,
        minSize: 150,
        accessorFn: row => row.recommendation?.profileName,
        cell: info => info.getValue() || '-',
      });
    }

    // Post Code column
    cols.push({
      accessorKey: 'postcode',
      header: 'POST CODE',
      size: 150,
      minSize: 150,
      cell: info => info.getValue() || '-',
    });

    // Phone column
    cols.push({
      accessorKey: 'phone',
      header: 'PHONE',
      size: 150,
      minSize: 150,
      cell: info => info.getValue() || '-',
    });

    // Comments column
    cols.push({
      accessorKey: 'comments',
      header: 'COMMENTS',
      size: 500,
      minSize: 500,
      cell: info => {
        const value = info.getValue();
        if (!value) return '-';
        return (
          <span className="whitespace-normal break-words">
            {value}
          </span>
        );
      },
    });

    return cols;
  }, [isAdmin, countries]);

  return {
    columns,
    formatDate,
    getCountryName
  };
};

export default useContactRequestColumns;
